import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { toMap } from '@icp/utils';
import { useAppContext } from '@icp/app';
import FormRenderer from '../../Wrapper';
import { EditableTableElement } from '../index';
import { ConditionalPropertyPropType } from '../../propTypes';
import {
  buildRowData,
  DISPLAYED_GROUPS_KEY,
  getInitialColumnDefs,
  getModification,
  getSelectedGroupTokens,
  getTableChangedData,
  GROUP_PREFIX,
} from './utils';

const PermissionGroupsBatchEditTable = forwardRef((props, ref) => {
  const { projectId, permissions, permissionGroups = [], readonly = false, style = {} } = props;

  const tableRef = useRef(null);
  const [data, setData] = useState(null);
  const { t } = useTranslation(['icp-form-renderer', 'icp-common']);
  const pbcList = useAppContext().pbcList;

  const selectedGroupsTokens = useMemo(
    () => getSelectedGroupTokens(permissionGroups),
    [permissionGroups],
  );

  const groupsMap = useMemo(
    () => toMap((group) => `${GROUP_PREFIX}${group.token}`)(permissionGroups),
    [permissionGroups],
  );

  const groupPermissionsMap = useMemo(
    () =>
      toMap(
        (group) => `${GROUP_PREFIX}${group.token}`,
        (group) => group.permissions.map((perm) => perm.token),
      )(permissionGroups),
    [permissionGroups],
  );

  const permissionsMap = useMemo(
    () => toMap((permission) => permission.token)(permissions),
    [permissions],
  );

  useImperativeHandle(ref, () => ({
    getChangedData: () => {
      // 不使用 EditableTableElement 的 getChangedData 方法，是因为尝试了各种办法都不能更新 initialValues
      const updatedData = getTableChangedData(tableRef.current?.gridApi, data?.rowData).updated;
      if (updatedData.length === 0) {
        return { modifiedGroups: [], differences: [] };
      }
      return getModification({
        permissionGroups,
        updatedData,
        groupPermissionsMap,
        permissionsMap,
      });
    },
  }));

  useEffect(() => {
    if (projectId == null || permissions == null || permissions.length === 0) {
      return;
    }
    const initData = {
      rowData: buildRowData({ permissions, permissionGroups, pbcList, readonly }),
      colDefs: getInitialColumnDefs(),
    };
    setData(initData);
  }, [pbcList, permissionGroups, permissions, projectId, readonly]);

  const columnDefs = useMemo(() => {
    const colDefs = data?.colDefs;
    if (!colDefs) return [];
    permissionGroups.forEach((group) => {
      colDefs.push({
        colId: `${GROUP_PREFIX}${group.token}`,
        field: `${GROUP_PREFIX}${group.token}`,
        headerName: group.name,
        hide: !selectedGroupsTokens.includes(group.token),
        editable: !readonly,
        cellRenderer: 'agCheckboxCellRenderer',
        onCellClicked: (params) => {
          if (readonly || params.node.group) {
            return;
          }
          const currentValue = params.data?.[params.colDef.field];
          params.node.setDataValue(params.colDef.field, !currentValue);
        },
      });
    });
    return colDefs;
  }, [data?.colDefs, permissionGroups, readonly, selectedGroupsTokens]);

  const gridOptions = useMemo(
    () => ({
      treeData: true,
      autoGroupColumnDef: {
        headerName: t('permission', { ns: 'icp-common' }),
        cellRendererParams: {
          suppressCount: true,
        },
      },
      enableClipboard: true,
      suppressStatusBarTotalRows: true,
      suppressColumnSelect: false,
      suppressClickEdit: true,
      rowGroupPanelShow: 'never',
      groupDefaultExpanded: 3,
      groupAllowUnbalanced: true,
      canAddRow: false,
      canDeleteRow: false,
      columnDefs,
      onColumnVisible: () => {
        const displayedGroupsTokens = tableRef.current?.gridApi
          .getColumns()
          .filter((column) => column.visible && column.colId.startsWith(GROUP_PREFIX))
          .map((column) => groupsMap[column.colId]?.token)
          .filter(Boolean);
        localStorage.setItem(
          DISPLAYED_GROUPS_KEY,
          displayedGroupsTokens ? JSON.stringify(displayedGroupsTokens) : null,
        );
      },
      processCellForClipboard: (params) => params.value, // 如果不实现 processCellForClipboard，将无法直接复制布尔值
      processCellFromClipboard: (params) => {
        if (
          typeof params.value === 'boolean' ||
          params.value === 'true' ||
          params.value === 'false'
        ) {
          return params.value === true || params.value === 'true';
        }
        return params.value;
      },
      onRowClicked: (event) => {
        const node = event.node;
        if (node.group) {
          node.setExpanded(!node.expanded);
        }
      },
      style: {
        height: '100%',
      },
    }),
    [columnDefs, groupsMap, t],
  );

  return data?.rowData ? (
    <FormRenderer defaultData={{ groupsPermissionSelection: data?.rowData }}>
      <EditableTableElement
        id="groupsPermissionSelection"
        ref={tableRef}
        style={style}
        componentProps={gridOptions}
      />
    </FormRenderer>
  ) : null;
});

PermissionGroupsBatchEditTable.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  permissions: PropTypes.arrayOf(PropTypes.shape({})),
  permissionGroups: PropTypes.arrayOf(PropTypes.shape({})),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
  style: PropTypes.shape({}),
};

export default PermissionGroupsBatchEditTable;
